import React, { useState, useEffect } from 'react';

interface MediaItem {
    type: 'video' | 'image';
    url: string;
}

interface ImageCarouselProps {
    media: MediaItem[];
    title: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ media, title }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    // Add effect to reload Twitter widgets when switching back to video
    useEffect(() => {
        // Only handle Twitter script for video items
        if (media[currentIndex].type === 'video') {
          const TWITTER_SCRIPT_SRC = "https://platform.twitter.com/widgets.js";
      
          // Check if the script already exists
          let existingScript = document.querySelector(`script[src="${TWITTER_SCRIPT_SRC}"]`);
      
          // Function to load script if not loaded
          const loadTwitterScript = () => {
            if (!existingScript) {
              const script = document.createElement("script");
              script.src = TWITTER_SCRIPT_SRC;
              script.async = true;
              document.body.appendChild(script);
      
              script.onload = () => {
                window.twttr?.widgets?.load();
              };
      
              existingScript = script;
            } else {
              // If script exists but widget not initialized, call load manually
              window.twttr?.widgets?.load();
            }
          };
      
          // Option 1: Load immediately
          // loadTwitterScript();
      
          // Option 2: Load when browser is idle (recommended for performance)
          if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(loadTwitterScript);
          } else {
            setTimeout(loadTwitterScript, 1500);
          }
      
          // Cleanup is not strictly needed here unless you want to remove the script:
          // If you want to remove the script when switching away from video, uncomment:
          /*
          return () => {
            const scriptToRemove = document.querySelector(`script[src="${TWITTER_SCRIPT_SRC}"]`);
            if (scriptToRemove) {
              scriptToRemove.remove();
            }
          };
          */
        }
      }, [currentIndex, media]);

    const goToNext = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === media.length - 1 ? 0 : prevIndex + 1
        );
    };

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? media.length - 1 : prevIndex - 1
        );
    };

    const renderMediaItem = (item: MediaItem) => {
        if (item.type === 'video') {
            return (
                <div className="w-full h-full flex items-center justify-center">
                    <div 
                        className="w-full max-w-xl mx-auto lg:w-[75%]"
                        style={{
                            position: 'relative',
                            height: 'fit-content',
                            margin: 0
                        }}
                    >
                        <div 
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            dangerouslySetInnerHTML={{ __html: item.url }}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="w-full h-full flex items-center justify-center p-1">
                    <div className="max-w-xl lg:w-[75%] h-auto flex items-center justify-center">
                        <img 
                            src={item.url} 
                            alt={`${title} - ${currentIndex + 1}`}
                            className="w-auto h-auto max-h-[70vh] object-contain"
                            style={{
                                maxWidth: '100%'
                            }}
                        />
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="relative w-full max-w-3xl lg:max-w-[75%] mx-auto bg-white rounded-lg overflow-hidden">
            <div className="min-h-[250px] lg:min-h-[350px] flex items-center justify-center">
                {/* Media Item */}
                {renderMediaItem(media[currentIndex])}
                
                {/* Navigation Arrows */}
                {media.length > 1 && (
                    <>
                        {/* Previous Button */}
                        <button 
                            onClick={goToPrevious}
                            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1 transition-colors z-20"
                            aria-label="Previous media"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" width={24} height={24} style={{minWidth:24,minHeight:24}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        {/* Next Button */}
                        <button 
                            onClick={goToNext}
                            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1 transition-colors z-20"
                            aria-label="Next media"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" width={24} height={24} style={{minWidth:24,minHeight:24}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>

                        {/* Media Counter - Reduced padding */}
                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-2 py-0.5 rounded-full text-xs z-20">
                            {currentIndex + 1} / {media.length}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ImageCarousel; 